// Developer Dashboard - System Administration
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, Users, Truck, Settings, Database, Activity, Tag, 
  Building2, FileText, Key, Upload, Plus, Trash2, Edit, 
  RefreshCw, ChevronRight, AlertTriangle, Check, X, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { developerApi, dienstApi, wacheApi } from '../lib/api';
import { useAuthStore, useWacheStore } from '../lib/store';
import { cn, roleLabels } from '../lib/utils';
import MainLayout from '../components/layout/MainLayout';
import { FeatureFlagsManager } from '../components/developer/FeatureFlagsManager';
import { HelmeSection } from '../components/developer/HelmeSection';
import { WacheLogoSection } from '../components/developer/WacheLogoSection';
import FahrzeugKategorienSection from '../components/developer/FahrzeugKategorienSection';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

// System Status Card
function SystemStatusCard({ status }) {
  if (!status) return null;
  
  return (
    <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-500/20">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-emerald-400">
          <Activity className="w-5 h-5" />
          System-Status
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-muted-foreground">Status</p>
            <p className="font-bold text-emerald-400 flex items-center gap-1">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              {status.status}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Datenbank</p>
            <p className="font-bold text-emerald-400">{status.database}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Benutzer</p>
            <p className="font-bold">{status.users_count}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Fahrzeuge</p>
            <p className="font-bold">{status.vehicles_count}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Wache Configuration Section
function WacheConfigSection({ wache, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: wache?.name || '',
    nummer: wache?.nummer || '',
    adresse: wache?.adresse || '',
  });

  useEffect(() => {
    if (wache) {
      setFormData({
        name: wache.name || '',
        nummer: wache.nummer || '',
        adresse: wache.adresse || '',
      });
    }
  }, [wache]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await developerApi.updateWache(formData);
      toast.success('Wache-Konfiguration gespeichert');
      setIsEditing(false);
      onUpdate?.();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Wache-Konfiguration
          </CardTitle>
          <CardDescription>Name, Nummer und Adresse der Feuerwache</CardDescription>
        </div>
        {!isEditing && (
          <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
            <Edit className="w-4 h-4 mr-2" />
            Bearbeiten
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Feuerwache Berlin-Mitte"
                />
              </div>
              <div className="space-y-2">
                <Label>Nummer</Label>
                <Input
                  value={formData.nummer}
                  onChange={(e) => setFormData(p => ({ ...p, nummer: e.target.value }))}
                  placeholder="4600"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input
                value={formData.adresse}
                onChange={(e) => setFormData(p => ({ ...p, adresse: e.target.value }))}
                placeholder="Musterstraße 1, 10115 Berlin"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Speichere...' : 'Speichern'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Name</p>
              <p className="font-medium">{wache?.name || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Nummer</p>
              <p className="font-medium">FW {wache?.nummer || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Adresse</p>
              <p className="font-medium">{wache?.adresse || '—'}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Users Management Section
function UsersSection({ users, onUpdate }) {
  const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, user: null });
  const [setRoleDialog, setSetRoleDialog] = useState({ open: false, user: null });
  const [isLoading, setIsLoading] = useState(false);
  const [newPassword, setNewPassword] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');

  const handleResetPassword = async () => {
    if (!resetPasswordDialog.user) return;
    setIsLoading(true);
    try {
      const res = await developerApi.resetPassword(resetPasswordDialog.user.id);
      setNewPassword(res.data.temporaeres_passwort);
      toast.success('Passwort zurückgesetzt');
      onUpdate?.();
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetRole = async () => {
    if (!setRoleDialog.user || !selectedRole) return;
    setIsLoading(true);
    try {
      await developerApi.setRole(setRoleDialog.user.id, selectedRole);
      toast.success(`Rolle auf ${selectedRole} gesetzt`);
      setSetRoleDialog({ open: false, user: null });
      onUpdate?.();
    } catch (error) {
      toast.error('Fehler beim Setzen der Rolle');
    } finally {
      setIsLoading(false);
    }
  };

  const roleOptions = [
    { value: 'MITARBEITER', label: 'Mitarbeiter' },
    { value: 'ADMIN', label: 'Admin' },
    { value: 'WACHABTEILUNGSLEITUNG', label: 'Wachabteilungsleitung' },
    { value: 'WACHLEITUNG', label: 'Wachleitung' },
  ];

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Benutzer-Verwaltung
          </CardTitle>
          <CardDescription>Alle Benutzer - Passwort-Reset und Rollenzuweisung</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Loginname</TableHead>
                  <TableHead>Rolle</TableHead>
                  <TableHead>Abteilung</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(user => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      {user.vorname} {user.nachname}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{user.loginname}</TableCell>
                    <TableCell>
                      <Badge variant={user.rolle === 'WACHLEITUNG' ? 'default' : 'secondary'}>
                        {roleLabels[user.rolle] || user.rolle}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {user.abteilung_id?.slice(0, 8) || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedRole(user.rolle);
                            setSetRoleDialog({ open: true, user });
                          }}
                        >
                          <Shield className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setNewPassword(null);
                            setResetPasswordDialog({ open: true, user });
                          }}
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Reset Password Dialog */}
      <Dialog 
        open={resetPasswordDialog.open} 
        onOpenChange={(open) => {
          if (!open) setNewPassword(null);
          setResetPasswordDialog({ open, user: open ? resetPasswordDialog.user : null });
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Passwort zurücksetzen
            </DialogTitle>
            <DialogDescription>
              Für: {resetPasswordDialog.user?.vorname} {resetPasswordDialog.user?.nachname}
            </DialogDescription>
          </DialogHeader>
          {newPassword ? (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
                Neues temporäres Passwort:
              </p>
              <p className="font-mono font-bold text-lg">{newPassword}</p>
              <p className="text-xs text-amber-600 mt-2">
                Der Benutzer muss das Passwort beim nächsten Login ändern.
              </p>
            </div>
          ) : (
            <p className="text-muted-foreground">
              Das aktuelle Passwort wird zurückgesetzt und ein neues temporäres Passwort generiert.
            </p>
          )}
          <DialogFooter>
            {newPassword ? (
              <Button onClick={() => setResetPasswordDialog({ open: false, user: null })}>
                Schließen
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setResetPasswordDialog({ open: false, user: null })}>
                  Abbrechen
                </Button>
                <Button onClick={handleResetPassword} disabled={isLoading}>
                  {isLoading ? 'Wird zurückgesetzt...' : 'Zurücksetzen'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Role Dialog */}
      <Dialog open={setRoleDialog.open} onOpenChange={(open) => setSetRoleDialog({ open, user: open ? setRoleDialog.user : null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Rolle ändern
            </DialogTitle>
            <DialogDescription>
              Für: {setRoleDialog.user?.vorname} {setRoleDialog.user?.nachname}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Neue Rolle</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetRoleDialog({ open: false, user: null })}>
              Abbrechen
            </Button>
            <Button onClick={handleSetRole} disabled={isLoading}>
              {isLoading ? 'Wird gespeichert...' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Vehicle Types Section with Image Upload
function FahrzeugTypenSection({ typen, onUpdate }) {
  const [uploadDialog, setUploadDialog] = useState({ open: false, typ: null });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = async () => {
    if (!selectedFile || !uploadDialog.typ) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      await developerApi.uploadFahrzeugBild(uploadDialog.typ.typ, formData);
      toast.success(`Bild für ${uploadDialog.typ.typ} hochgeladen`);
      // Cache invalidieren → Custom-Bilder neu laden (mit Cache-Buster)
      const { loadCustomFahrzeugBilder } = await import('../lib/fahrzeugBilder');
      await loadCustomFahrzeugBilder(true);
      setUploadDialog({ open: false, typ: null });
      setSelectedFile(null);
      onUpdate?.();
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5" />
            Fahrzeugtypen & Bilder
          </CardTitle>
          <CardDescription>Fahrzeugtypen konfigurieren und Bilder hochladen</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {typen.map(typ => (
              <div 
                key={typ.typ}
                className="relative group p-4 rounded-xl border bg-card hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => setUploadDialog({ open: true, typ })}
              >
                <div className="aspect-video mb-3 rounded-lg bg-muted/50 overflow-hidden flex items-center justify-center">
                  {typ.bild_pfad ? (
                    <img 
                      src={typ.bild_pfad} 
                      alt={typ.typ}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Truck className="w-8 h-8 text-muted-foreground/30" />
                  )}
                </div>
                <p className="font-bold text-sm">{typ.typ}</p>
                <p className="text-xs text-muted-foreground truncate">{typ.bezeichnung}</p>
                <div className="absolute inset-0 bg-black/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Upload className="w-6 h-6 text-white" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog.open} onOpenChange={(open) => {
        if (!open) setSelectedFile(null);
        setUploadDialog({ open, typ: open ? uploadDialog.typ : null });
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Bild hochladen für {uploadDialog.typ?.typ}
            </DialogTitle>
            <DialogDescription>
              {uploadDialog.typ?.bezeichnung}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {uploadDialog.typ?.bild_pfad && (
              <div className="aspect-video rounded-lg bg-muted overflow-hidden">
                <img 
                  src={uploadDialog.typ.bild_pfad} 
                  alt={uploadDialog.typ.typ}
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Neues Bild (PNG, JPEG, WebP)</Label>
              <Input 
                type="file" 
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog({ open: false, typ: null })}>
              Abbrechen
            </Button>
            <Button onClick={handleUpload} disabled={!selectedFile || isUploading}>
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Hochladen...
                </>
              ) : (
                'Hochladen'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Tags Section
function TagsSection({ tags, onUpdate }) {
  const [addDialog, setAddDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [newTag, setNewTag] = useState({ name: '', farbe: 'blue' });

  const colors = [
    { value: 'red', label: 'Rot', class: 'bg-red-500' },
    { value: 'blue', label: 'Blau', class: 'bg-blue-500' },
    { value: 'green', label: 'Grün', class: 'bg-green-500' },
    { value: 'yellow', label: 'Gelb', class: 'bg-yellow-500' },
    { value: 'purple', label: 'Lila', class: 'bg-purple-500' },
    { value: 'orange', label: 'Orange', class: 'bg-orange-500' },
    { value: 'gray', label: 'Grau', class: 'bg-gray-500' },
  ];

  const handleCreate = async () => {
    if (!newTag.name) return;
    setIsLoading(true);
    try {
      await developerApi.createTag(newTag);
      toast.success('Tag erstellt');
      setAddDialog(false);
      setNewTag({ name: '', farbe: 'blue' });
      onUpdate?.();
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (tagId) => {
    try {
      await developerApi.deleteTag(tagId);
      toast.success('Tag gelöscht');
      onUpdate?.();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5" />
              Tags
            </CardTitle>
            <CardDescription>Globale Tags für Mitarbeiter und Fahrzeuge</CardDescription>
          </div>
          <Button size="sm" onClick={() => setAddDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Tag erstellen
          </Button>
        </CardHeader>
        <CardContent>
          {tags.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Keine Tags vorhanden</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {tags.map(tag => (
                <Badge 
                  key={tag.id}
                  variant="outline"
                  className="group pr-1 gap-2"
                >
                  <span 
                    className={cn('w-2 h-2 rounded-full', colors.find(c => c.value === tag.farbe)?.class || 'bg-gray-500')}
                  />
                  {tag.name}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleDelete(tag.id)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Tag Dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neuen Tag erstellen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input 
                value={newTag.name}
                onChange={(e) => setNewTag(p => ({ ...p, name: e.target.value }))}
                placeholder="z.B. Atemschutz"
              />
            </div>
            <div className="space-y-2">
              <Label>Farbe</Label>
              <div className="flex gap-2">
                {colors.map(color => (
                  <button
                    key={color.value}
                    onClick={() => setNewTag(p => ({ ...p, farbe: color.value }))}
                    className={cn(
                      'w-8 h-8 rounded-full transition-all',
                      color.class,
                      newTag.farbe === color.value && 'ring-2 ring-offset-2 ring-primary'
                    )}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog(false)}>Abbrechen</Button>
            <Button onClick={handleCreate} disabled={isLoading || !newTag.name}>
              {isLoading ? 'Erstelle...' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Audit Logs Section
function AuditLogsSection({ logs }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Audit-Log
        </CardTitle>
        <CardDescription>Letzte System-Aktivitäten</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[300px]">
          <div className="space-y-2">
            {logs.map((log, i) => (
              <div key={i} className="flex items-start gap-3 p-2 rounded hover:bg-muted/50">
                <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{log.beschreibung}</p>
                  <p className="text-xs text-muted-foreground">
                    {log.benutzer_name || 'System'} • {new Date(log.zeitpunkt).toLocaleString('de-DE')}
                  </p>
                </div>
                <Badge variant="outline" className="shrink-0 text-xs">
                  {log.aktion}
                </Badge>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

// System Tools Section
function DbCleanupBlock() {
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [stats, setStats] = useState(null);

  const runDryRun = async () => {
    setIsLoading(true);
    setPreview(null);
    try {
      const [statsRes, dryRes] = await Promise.all([
        developerApi.getDbStats(),
        developerApi.cleanupEinteilungen(true),
      ]);
      setStats(statsRes.data);
      setPreview(dryRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Analysieren');
    } finally {
      setIsLoading(false);
    }
  };

  const runCleanup = async () => {
    if (!preview || preview.total_betroffen === 0) return;
    if (!confirm(`Wirklich ${preview.total_betroffen} Einteilungen LÖSCHEN? Diese Aktion ist nicht rückgängig zu machen.`)) return;
    setIsLoading(true);
    try {
      const res = await developerApi.cleanupEinteilungen(false);
      toast.success(`${res.data.geloescht ?? 0} Einteilungen entfernt`);
      setPreview(res.data);
      // Stats neu laden
      const statsRes = await developerApi.getDbStats();
      setStats(statsRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Löschen');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-4 border rounded-lg space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="font-medium flex items-center gap-2">
            <Database className="w-4 h-4" />
            Datenbank-Bereinigung (Einteilungen)
          </h4>
          <p className="text-sm text-muted-foreground mt-1">
            Findet und entfernt verwaiste / korrupte Einträge: ohne Mitarbeiter,
            mit gelöschtem Fahrzeug, leere Karteileichen (keine Funktion + kein Status)
            und Duplikate (selber MA + Datum + Dienstart).
          </p>
        </div>
        <Button onClick={runDryRun} disabled={isLoading} variant="outline" size="sm" data-testid="db-cleanup-analyze">
          {isLoading ? 'Analysiere…' : 'Analyse starten'}
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          {Object.entries(stats).map(([k, v]) => (
            <div key={k} className="px-2 py-1 rounded bg-muted/40 flex justify-between">
              <span className="text-muted-foreground">{k}</span>
              <span className="font-mono font-semibold">{v}</span>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div className="space-y-2 text-sm">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {[
              { key: 'ohne_mitarbeiter', label: 'Ohne Mitarbeiter' },
              { key: 'verwaister_mitarbeiter', label: 'MA gelöscht/inaktiv' },
              { key: 'verwaistes_fahrzeug', label: 'FZ gelöscht' },
              { key: 'leere_eintraege', label: 'Leere Einträge' },
              { key: 'duplikate', label: 'Duplikate' },
            ].map(({ key, label }) => {
              const count = preview[key]?.length || 0;
              return (
                <div key={key} className={cn(
                  'p-2 rounded border text-center',
                  count > 0 ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'
                )}>
                  <div className="text-lg font-bold font-mono">{count}</div>
                  <div className="text-[10px] text-muted-foreground">{label}</div>
                </div>
              );
            })}
          </div>
          <div className={cn(
            'flex items-center justify-between p-3 rounded-lg border-2',
            preview.total_betroffen > 0 ? 'border-amber-400 bg-amber-50' : 'border-emerald-400 bg-emerald-50'
          )}>
            <div>
              <p className="font-semibold">
                {preview.total_betroffen > 0
                  ? `${preview.total_betroffen} Einträge können bereinigt werden`
                  : '✓ Datenbank ist sauber'}
              </p>
              {preview.geloescht != null && (
                <p className="text-xs text-emerald-700">{preview.geloescht} Einträge wurden gelöscht</p>
              )}
            </div>
            {preview.total_betroffen > 0 && (
              <Button onClick={runCleanup} disabled={isLoading} variant="destructive" size="sm" data-testid="db-cleanup-execute">
                Jetzt bereinigen
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SystemToolsSection({ onUpdate }) {
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  const handleSeedFahrzeugtypen = async () => {
    setIsSeeding(true);
    setSeedResult(null);
    try {
      const res = await dienstApi.seedFahrzeugtypen();
      setSeedResult({
        success: true,
        message: res.data.message || `${res.data.created_count || 0} Fahrzeugtypen erstellt`
      });
      toast.success('Fahrzeugtypen erfolgreich initialisiert');
      onUpdate?.();
    } catch (error) {
      setSeedResult({
        success: false,
        message: error.response?.data?.detail || 'Fehler beim Initialisieren'
      });
      toast.error('Fehler beim Initialisieren der Fahrzeugtypen');
    } finally {
      setIsSeeding(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="w-5 h-5" />
          System-Tools
        </CardTitle>
        <CardDescription>Datenbank-Initialisierung und Wartung</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Fahrzeugtypen Seeding */}
        <div className="p-4 border rounded-lg space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-medium flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Fahrzeugtypen initialisieren
              </h4>
              <p className="text-sm text-muted-foreground mt-1">
                Erstellt die Standard-Fahrzeugtypen (LHF, RTW, DLK, NEF, etc.) falls diese noch nicht existieren.
                Wird benötigt, um neue Fahrzeuge anlegen zu können.
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={handleSeedFahrzeugtypen} 
                disabled={isSeeding}
                variant="outline"
              >
                {isSeeding ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Initialisiere...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Initialisieren
                  </>
                )}
              </Button>
              <Button 
                onClick={async () => {
                  if (!confirm('Alle Fahrzeugtypen löschen und neu erstellen? Alte Typen wie RTW, KTW werden entfernt.')) return;
                  setIsSeeding(true);
                  try {
                    const res = await dienstApi.resetFahrzeugtypen();
                    setSeedResult({ success: true, message: res.data.message });
                    toast.success('Fahrzeugtypen zurückgesetzt');
                  } catch (err) {
                    setSeedResult({ success: false, message: err.response?.data?.detail || 'Fehler' });
                    toast.error('Fehler beim Zurücksetzen');
                  } finally {
                    setIsSeeding(false);
                  }
                }} 
                disabled={isSeeding}
                variant="destructive"
                size="sm"
              >
                Reset
              </Button>
            </div>
          </div>
          {seedResult && (
            <div className={cn(
              "p-3 rounded-lg text-sm flex items-center gap-2",
              seedResult.success 
                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400"
                : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
            )}>
              {seedResult.success ? (
                <Check className="w-4 h-4" />
              ) : (
                <AlertTriangle className="w-4 h-4" />
              )}
              {seedResult.message}
            </div>
          )}
        </div>

        {/* DB-Cleanup für Einteilungen */}
        <DbCleanupBlock />

      </CardContent>
    </Card>
  );
}

// Main Developer Page
export function DeveloperPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({
    status: null,
    wache: null,
    users: [],
    fahrzeugTypen: [],
    tags: [],
    auditLogs: [],
  });

  // Check developer access
  useEffect(() => {
    if (user?.rolle !== 'DEVELOPER') {
      navigate('/dashboard');
      toast.error('Kein Zugriff auf Developer-Bereich');
    }
  }, [user, navigate]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [statusRes, wacheRes, usersRes, typenRes, tagsRes, logsRes] = await Promise.all([
        developerApi.getSystemStatus(),
        developerApi.getWache(),
        developerApi.getUsers(),
        developerApi.getFahrzeugTypen(),
        developerApi.getTags(),
        developerApi.getAuditLogs(50),
      ]);

      setData({
        status: statusRes.data,
        wache: wacheRes.data,
        users: usersRes.data,
        fahrzeugTypen: typenRes.data,
        tags: tagsRes.data,
        auditLogs: logsRes.data,
      });
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (user?.rolle !== 'DEVELOPER') {
    return null;
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold flex items-center gap-3">
              <Shield className="w-8 h-8 text-amber-500" />
              Developer Dashboard
            </h1>
            <p className="text-muted-foreground">
              System-Administration & Konfiguration
            </p>
          </div>
          <Button variant="outline" onClick={loadData} disabled={isLoading}>
            <RefreshCw className={cn("w-4 h-4 mr-2", isLoading && "animate-spin")} />
            Aktualisieren
          </Button>
        </div>

        {/* System Status */}
        <SystemStatusCard status={data.status} />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-4 sm:grid-cols-7 h-auto">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Übersicht</span>
            </TabsTrigger>
            <TabsTrigger value="menu" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span className="hidden sm:inline">Menüs</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Benutzer</span>
            </TabsTrigger>
            <TabsTrigger value="vehicles" className="flex items-center gap-2">
              <Truck className="w-4 h-4" />
              <span className="hidden sm:inline">Fahrzeuge</span>
            </TabsTrigger>
            <TabsTrigger value="tags" className="flex items-center gap-2">
              <Tag className="w-4 h-4" />
              <span className="hidden sm:inline">Tags</span>
            </TabsTrigger>
            <TabsTrigger value="tools" className="flex items-center gap-2">
              <Database className="w-4 h-4" />
              <span className="hidden sm:inline">Tools</span>
            </TabsTrigger>
            <TabsTrigger value="logs" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Audit-Log</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <WacheConfigSection wache={data.wache} onUpdate={loadData} />
            <WacheLogoSection />
          </TabsContent>

          <TabsContent value="menu">
            <FeatureFlagsManager />
          </TabsContent>

          <TabsContent value="users">
            <UsersSection users={data.users} onUpdate={loadData} />
          </TabsContent>

          <TabsContent value="vehicles">
            <div className="space-y-4">
              <FahrzeugTypenSection typen={data.fahrzeugTypen} onUpdate={loadData} />
              <FahrzeugKategorienSection />
              <HelmeSection />
            </div>
          </TabsContent>

          <TabsContent value="tags">
            <TagsSection tags={data.tags} onUpdate={loadData} />
          </TabsContent>

          <TabsContent value="tools">
            <SystemToolsSection onUpdate={loadData} />
          </TabsContent>

          <TabsContent value="logs">
            <AuditLogsSection logs={data.auditLogs} />
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}

export default DeveloperPage;
